import { PulseAttributeMessage, TransformPipe } from '@pulse/common';

/**
 * Add support for setting multiple $overwrite attributes in a single call.
 *
 * Transforms:
 * ```
 * { did: 'a', sid: 'b' }
 * ```
 * into:
 * ```
 * [
 *     { operation: '$overwrite', name: 'did', value: 'a' },
 *     { operation: '$overwrite', name: 'sid', value: 'b' }
 * ]
 * ```
 *
 * Will not perform transformation if the data is already in the desired form.
 */
export class AttributeSpreadPipe implements TransformPipe<PulseAttributeMessage> {
    transform(message: PulseAttributeMessage): PulseAttributeMessage[] {
        const attribute = message.data;

        if (typeof attribute !== 'object' || !attribute || Array.isArray(attribute)) {
            return [];
        }

        const isAttribute =
            typeof attribute === 'object' &&
            typeof attribute?.name === 'string' &&
            ['$overwrite', '$inc', '$dec'].indexOf(attribute?.type) > -1;

        if (!isAttribute && typeof attribute === 'object' && attribute) {
            const attributes: PulseAttributeMessage[] = [];

            for (const name in attribute) {
                if (!Object.prototype.hasOwnProperty.call(attribute, name)) {
                    continue;
                }

                const value = (attribute as any)[name];
                const valid =
                    typeof value === 'string' ||
                    typeof value === 'number' ||
                    typeof value === 'boolean';

                if (!valid) {
                    continue;
                }

                const expandedAttribute: PulseAttributeMessage = {
                    ...message,
                    data: { type: '$overwrite', name, value },
                };

                attributes.push(expandedAttribute);
            }

            return attributes;
        }

        return [message];
    }
}
